import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useCBSBatchStore } from '@/stores/cbsBatchStore';
import { useCreateDailyRecord } from '@/hooks/api/cbs';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { MortalityCause } from '@/types/cbs';
import { getAuthenticatedClient } from '@/services/client';
import { NoBatchPlaceholder, MortalityCausePicker } from '@/components/cbs';
import { useSimpleMode } from '@/contexts/SimpleModeContext';
import { SimpleMetric } from '@/components/cbs/SimpleMetric';
import { Skull, Minus, Plus, Camera, Check, Loader2 } from 'lucide-react';
import { useInlineCamera } from '@/hooks/useInlineCamera';

export const MortalityLogScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { activeBatch } = useCBSBatchStore();
  const createRecord = useCreateDailyRecord();
  const queryClient = useQueryClient();
  const { isSimpleMode } = useSimpleMode();

  const [count, setCount] = useState(0);
  const [cause, setCause] = useState<MortalityCause | undefined>();
  const inlineCamera = useInlineCamera('mortality');

  if (!activeBatch) {
    return (
      <Screen topBarProps={{ title: isSimpleMode ? '\uD83D\uDC80' : t('cbs.mortality.title', { defaultValue: '\uD83D\uDC80 Mortality Log' }), showBack: true, showMapPin: false, showSettings: false }} showBottomNav={false}>
        <NoBatchPlaceholder icon={Skull} />
      </Screen>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const batchDay = Math.max(1, Math.floor((Date.now() - new Date(activeBatch.placement_date).getTime()) / 86400000) + 1);
  const maxMortality = (activeBatch.current_bird_count ?? activeBatch.doc_count) - (activeBatch.cumulative_mortality ?? 0);

  const increment = (amount: number) => setCount(prev => Math.min(prev + amount, maxMortality));
  const decrement = (amount: number) => setCount(prev => Math.max(prev - amount, 0));

  const handleSubmit = async () => {
    try {
      const client = getAuthenticatedClient();
      
      // Check if today's record already exists
      const { data: existingRecord } = await client
        .from('daily_records')
        .select('id')
        .eq('batch_id', activeBatch.id)
        .eq('record_date', today)
        .maybeSingle();

      if (existingRecord) {
        // UPDATE existing record with mortality data only
        const { error } = await client
          .from('daily_records')
          .update({
            mortality_count: count,
            mortality_cause: cause || null,
            culling_count: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingRecord.id);

        if (error) throw error;

        // Keep home-screen ring + related views in sync with the direct UPDATE.
        // The createRecord path invalidates these in onSettled; the UPDATE
        // path bypasses the mutation hook, so we invalidate manually.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['cbs', 'dailyRecords', activeBatch.id] }),
          queryClient.invalidateQueries({ queryKey: ['cbs', 'batch', 'active'] }),
          queryClient.invalidateQueries({ queryKey: ['cbs', 'dailyRecord', 'today'] }),
          queryClient.invalidateQueries({ queryKey: ['cbs', 'today-task-status'] }),
        ]);

        toast({ title: 'Day logged!', description: 'Mortality record updated' });
      } else {
        // No record yet today — INSERT new one
        await createRecord.mutateAsync({
          batch_id: activeBatch.id,
          user_id: activeBatch.user_id,
          country_id: activeBatch.country_id,
          record_date: today,
          batch_day: batchDay,
          mortality_count: count,
          mortality_cause: cause || null,
          culling_count: null,
          feed_consumed_kg: 0,
          feed_type: null,
          water_consumed_liters: null,
          temperature_min_c: null,
          temperature_max_c: null,
          humidity_pct: null,
          weight_sample_count: null,
          weight_sample_avg_g: null,
          production_stage: null,
          is_synced: false,
          recorded_at: new Date().toISOString(),
          recorded_by: null,
        });
      }

      navigate('/');
    } catch {
      toast({ title: 'Failed to save', description: 'Please try again', variant: 'destructive' });
    }
  };

  // handleTakePhoto removed — using inlineCamera hook instead

  return (
    <Screen
      topBarProps={{
        title: isSimpleMode ? '\u{1F480}' : t('cbs.mortality.title', { defaultValue: '\u{1F480} Mortality Log' }),
        showBack: true,
        showMapPin: false,
        showSettings: false,
        onBack: () => navigate('/'),
      }}
      showBottomNav={false}
    >
      <div className="p-4 space-y-6">
        {/* Day indicator in simple mode */}
        {isSimpleMode && (
          <SimpleMetric emoji="📅" value={`Day ${batchDay}`} size="md" color="info" />
        )}

        {/* Large Visual Counter */}
        <Card className="p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className={cn(isSimpleMode ? "text-5xl" : "hidden")}>💀</span>
            {!isSimpleMode && <Skull className="w-8 h-8 text-destructive" />}
            {!isSimpleMode && <span className="text-lg font-medium">{t('cbs.mortality.howMany')}</span>}
          </div>

          {/* Big Number Display */}
          <div className={cn(
            "font-bold text-foreground mb-6",
            isSimpleMode ? "text-8xl" : "text-7xl"
          )}>
            {count}
          </div>

          {/* Large +/- Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Button
              variant="outline"
              className={cn(isSimpleMode ? "h-24 text-4xl" : "h-20 text-3xl")}
              onClick={() => decrement(1)}
              disabled={count === 0}
            >
              <Minus className={cn(isSimpleMode ? "w-12 h-12" : "w-10 h-10")} />
            </Button>
            <Button
              variant="outline"
              className={cn(isSimpleMode ? "h-24 text-4xl" : "h-20 text-3xl")}
              onClick={() => increment(1)}
              disabled={count >= maxMortality}
            >
              <Plus className={cn(isSimpleMode ? "w-12 h-12" : "w-10 h-10")} />
            </Button>
          </div>

          {/* Quick Add Buttons */}
          <div className="grid grid-cols-4 gap-2">
            {[5, 10, 20, 50].map((num) => (
              <Button
                key={num}
                variant="secondary"
                className={cn("font-bold", isSimpleMode ? "h-16 text-2xl" : "h-14 text-xl")}
                onClick={() => increment(num)}
                disabled={count + num > maxMortality}
              >
                +{num}
              </Button>
            ))}
          </div>

          {/* Zero Quick Action */}
          <Button
            variant="ghost"
            className={cn("mt-4 text-success", isSimpleMode ? "text-xl h-14" : "text-lg")}
            onClick={() => setCount(0)}
          >
            0️⃣ {!isSimpleMode && t('cbs.mortality.noDeaths')}
          </Button>
        </Card>

        {/* Cause Selection */}
        {count > 0 && (
          <Card className="p-4">
            {!isSimpleMode && (
              <h3 className="text-lg font-medium mb-4 text-center">
                {t('cbs.mortality.whyDied')}
              </h3>
            )}
            <MortalityCausePicker value={cause} onChange={setCause} />
          </Card>
        )}

        {/* Photo Capture */}
        {count > 0 && (
          <Card className={cn("p-4", !inlineCamera.photoTaken && "border-destructive")}>
            <input
              ref={inlineCamera.fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={inlineCamera.handleFileSelected}
            />
            <Button
              variant={inlineCamera.photoTaken ? "default" : "destructive"}
              className={cn("w-full gap-3", isSimpleMode ? "h-24 text-2xl" : "h-20 text-lg")}
              onClick={inlineCamera.openCamera}
              disabled={inlineCamera.isUploading}
            >
              {inlineCamera.isUploading ? (
                <><Loader2 className="w-8 h-8 animate-spin" />{!isSimpleMode && 'Uploading...'}</>
              ) : inlineCamera.photoTaken ? (
                <>{isSimpleMode ? '✅ 📸' : <><Check className="w-8 h-8" />{t('cbs.dailyLog.photos.captured')}</>}</>
              ) : (
                <>{isSimpleMode ? '📸❗' : <><Camera className="w-8 h-8" />{t('cbs.mortality.takePhoto')}</>}</>
              )}
            </Button>
            {!inlineCamera.photoTaken && !isSimpleMode && (
              <p className="text-sm text-destructive text-center mt-2">
                {t('cbs.mortality.photoRequired')}
              </p>
            )}
          </Card>
        )}

        {/* Submit Button */}
        <Button
          className={cn("w-full gap-2", isSimpleMode ? "h-20 text-2xl" : "h-16 text-xl")}
          onClick={handleSubmit}
          disabled={createRecord.isPending || (count > 5 && !inlineCamera.photoTaken)}
        >
          {createRecord.isPending ? (
            <><span className="animate-spin">⏳</span> {!isSimpleMode && t('common.saving')}</>
          ) : (
            <>
              {isSimpleMode ? '✅' : <Check className="w-6 h-6" />}
              {!isSimpleMode && t('common.save')}
            </>
          )}
        </Button>
      </div>
    </Screen>
  );
};

export default MortalityLogScreen;
